
import NetworkImage from '~/components/global/NetworkImage';
import Share from '~/components/modal/Share';
import Transcriptions from '~/components/global/Transcriptions';
import generateMetaData from '../../features/metaDataGenerator';
import PhotoClient from '~/clients/photoClient';

export default {
    name: 'Post',
    components: {
        Transcriptions,
        NetworkImage,
    },
    data() {
        return {
            defaultBackgroundColor: '#3EC6A1',
            defaultForegroundColor: '#FFFFFF',
            audio: undefined,
            audioDuration: 0,
            audioLoaded: false,
            playing: false,
            audioCurrentTime: 0,
            isScrubbing: false,
            mouseScrubData: undefined,
            didUserClickPlayButton: false,
        };
    },
    props: {
        post: Object,
    },
    mounted() {
        this.$analytics.trackEvent('post_view', this.$analytics.postParams(this.post));

        this.audio = new Audio(this.post.audio.url);
        this.audio.addEventListener('loadeddata', this.loaded);
    },
    head() {
        const name = this.profile.name || this.profile.url;
        const fallbackDescription = `Listen to ${name}'s latest voices`;

        return generateMetaData({
            oEmbed: true,
            url: this.$store.getters['app/getFullyQualifiedURL'],
            applicationNameTitle: name,
            title: `${name} - ${this.post.caption || 'Listen now'}`,
            description: this.post.rawTranscript || fallbackDescription,
            image: this.post.image || this.profile.image,
            videoURL: this.post.shareVideoWithTranscriptURL,
            campaign: this.$route.query.ct,
            addPlayButtonOnShareImage: true,
            faviconImage: this.profile.customFaviconImage,
        });
    },
    methods: {
        loaded() {
            if (this.audio.readyState >= 2) {
                this.audioLoaded = true;
                this.audioDuration = this.audio.duration;
            } else {
                throw new Error('Failed to load sound file.');
            }
        },
        update() {
            this.audioCurrentTime = this.audio.currentTime;
        },
        onEnded() {
            this.$analytics.trackEvent('audioPlayEnd', this.$analytics.playParams('completed', { post: this.post, player: this.audio }));
            this.audio.removeEventListener('timeupdate', this.update);
            this.audio.removeEventListener('ended', this.onEnded);
            this.audioCurrentTime = 0;
            this.playing = false;
        },
        togglePlayPause() {
            if (this.playing) {
                this.$analytics.trackEvent('audioPlayStateChanged', 'pausing');
                this.pause();
            } else {
                this.$analytics.trackEvent('audioPlayStateChanged', 'playing');
                this.play();
            }
        },
        play() {
            if (!this.didUserClickPlayButton) {
                this.didUserClickPlayButton = true;
                this.$analytics.trackEvent('audioPlayBegin', this.$analytics.playParams('began', { post: this.post, player: this.audio }));
            }

            this.playing = true;
            this.audio.play();
            this.audio.addEventListener('timeupdate', this.update);
            this.audio.addEventListener('ended', this.onEnded);
        },
        pause() {
            this.playing = false;
            this.audio.pause();
        },
        onScrubberMouseDown(event) {
            const eventTarget = event.target;
            if (!eventTarget) {
                return;
            }

            this.isScrubbing = true;

            const elementWidth = this.$refs['progressBar'].clientWidth;
            const clickedXOffset = event.offsetX;

            // click on progress point is disabled
            if (eventTarget.id !== 'progress-point') {
                this.seek(clickedXOffset / elementWidth);
            }

            // sum of progress container and progressbar offset
            const minValue = this.$refs['progressBar'].getBoundingClientRect().left;
            const maxValue = minValue + elementWidth;
            this.mouseScrubData = {
                minValue: minValue,
                maxValue: maxValue,
                isPaused: !this.playing,
            };

            this.pause();

            document.addEventListener('mousemove', this.onScrubberMouseMove);
            document.addEventListener('mouseup', this.onScrubberMouseUp);
        },
        onScrubberMouseMove(event) {
            if (!this.mouseScrubData) {
                return;
            }

            let offset = 0;
            if (event.clientX < this.mouseScrubData.minValue) {
                offset = 0;
            } else if (event.clientX > this.mouseScrubData.maxValue) {
                offset = 1;
            } else {
                const width = this.mouseScrubData.maxValue - this.mouseScrubData.minValue;
                const position = event.clientX - this.mouseScrubData.minValue;
                offset = position / width;
            }

            this.seek(offset);
        },
        onScrubberMouseUp() {
            document.removeEventListener('mousemove', this.onScrubberMouseMove);
            document.removeEventListener('mouseup', this.onScrubberMouseUp);
            this.isScrubbing = false;

            if (!this.mouseScrubData.isPaused) {
                this.audio.play();
                this.playing = true;
            }
        },
        seek(seekOffset) {
            if (this.audio) {
                this.audio.currentTime = this.audio.duration * seekOffset;
                this.audioCurrentTime = this.audio.currentTime;
            }
        },
        showShareModal() {
            this.$analytics.trackEvent('share_tapped', this.$analytics.postParams(this.post));
            this.$store.commit('ui/showModal', {
                component: 'Share',
                props: {
                    url: this.post.shareURL,
                    post: this.post,
                    title: `Share @${this.post.profile.url}ʼs post`,
                    subtitle: 'Make someone elseʼs day and share!',
                    shareVideoUrl: this.shareVideoUrl,
                    analytics: {
                        ...{},
                        ...this.$analytics.postParams(this.post),
                    },
                },
            });
        },
    },
    computed: {
        profile() {
            return this.post.profile;
        },
        progress() {
            const progress = (this.audioCurrentTime / this.audioDuration) * 100;
            if (progress > 100) {
                return 100;
            }

            return progress;
        },
        shareVideoUrl() {
            if (this.post) {
                return this.post.shareVideoWithTranscriptURL;
            }

            return null;
        },
        brandColor() {
            return this.profile.brandColor ?? this.defaultBackgroundColor;
        },
        getForegroundColor() {
            if (!this.profile.brandColor) {
                return this.defaultForegroundColor;
            }

            const brandColorHex = PhotoClient.getRgbColorFromHex(this.profile.brandColor);
            const foregroundColorHex = PhotoClient.getRgbColorFromHex(this.defaultForegroundColor);
            const contrast = PhotoClient.getContrast(brandColorHex, foregroundColorHex);
            if (contrast < 3) {
                return '#000000';
            }

            return this.defaultForegroundColor;
        },
    },
};
